import { CommonModule, formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  CBox_PublicPaginatedResponse,
  CBox_PublicSuccessResponse,
} from '@server/services/cbox/public/api/v1/resources/common/request_base/types';
import {
  CBox_AdminLockerModuleDataResponse,
  CBox_AdminModuleListItemDataResponse,
} from '@server/services/cbox/public/api/v1/resources/internal/locker_module/types';
import {
  CBox_AdminGetTodoListDataItem,
  CBox_AdminGetTodoListDataParams,
} from '@server/services/cbox/public/api/v1/resources/internal/todo/types';
import { CBoxAdminEquipmentCreateDialogComponent } from 'app/profile/admin/components/dialogs/create/cbox-admin-equipment-create.component';
import { CBoxAdminModuleInfoComponent } from 'app/profile/admin/modules/dialogs/info/cbox-admin-module-info.component';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import {
  catchError,
  first,
  forkJoin,
  interval,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { ApiService, handlePublicApiError } from 'src/services/api/api.service';
import { CBoxToDoListComponent } from '../../../../../shared/to-do/list/cbox-to-do-list.component';
import { CBoxToDoCreateDialogComponent } from 'app/shared/to-do/create/cbox-to-do-create-dialog.component';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { ToastService } from 'src/services/toast/toast.service';
import { CBox_AdminLockerData } from '@server/services/cbox/public/api/v1/resources/internal/locker/types';
import { CBoxAdminLockerConfigDialogComponent } from '../config/cbox-admin-locker-config-dialog.component';
import {
  formatTimeInMilliseconds,
  hasMinutesPassedSince,
  lockerCleanAddress,
} from 'src/helpers/functions';
import { CBoxProfileLockerUpdateConfirmationDialogComponent } from '../update-confirmation/cbox-profile-locker-update-confirmation-dialog.component';
import { MeterGroupModule, MeterItem } from 'primeng/metergroup';
import { TagModule } from 'primeng/tag';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FailedEventsTableComponent } from './failed-events/cbox-admin-failed-events-table.component';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { DividerModule } from 'primeng/divider';
import { CBoxAdminLockerEquipmentStatusUpdateDialogComponent } from '../equipment-status-update/cbox-admin-locker-equipment-status-update-dialog.component';
import { TooltipModule } from 'primeng/tooltip';
import {
  CboxLockerPreviewComponent,
  LockerPreviewItemData,
} from '../../../components/locker-preview/cbox-locker-preview.component';
import { PaginatorModule } from 'primeng/paginator';
import {
  CBoxPublicAdminGetCommandStatusListParams,
  CBoxPublicAdminGetCommandStatusListResponse,
} from '@server/services/cbox/public/api/v1/resources/internal/boxes/list/types';
import { CBoxAdminLockerCommandDetailsComponent } from './command-details/cbox-admin-locker-command-details.component';
import { CBox_AdminEquipmentListItem } from '@server/services/cbox/public/api/v1/resources/internal/equipment/types';
import { CboxAdminLockerActionConfirmationDialogComponent } from '../action-confirmation/cbox-admin-locker-action-confirmation-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export enum LockerAdminOpenDoorActionLogState {
  INITIAL = 'INITIAL',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED_SUCCESS = 'FINISHED_SUCCESS',
  FINISHED_ERROR = 'FINISHED_ERROR',
  FINISHED_TIMEOUT = 'FINISHED_TIMEOUT',
  CANCELED = 'CANCELED',
}

export enum LockerAdminActionLogState {
  INITIAL = 'INITIAL',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED_SUCCESS = 'FINISHED_SUCCESS',
  FINISHED_ERROR = 'FINISHED_ERROR',
  CANCELED = 'CANCELED',
}

type ModuleEquipmentType =
  CBox_AdminLockerModuleDataResponse['equipments'][number] & {
    module: {
      serial: string;
      id: number;
    };
  };

export type CBox_LockerPublicStructure = {
  modules: {
    position: number;
    positionRelativeToMain: 'MAIN' | 'LEFT' | 'RIGHT';
    assets: {
      type: string;
      content: string;
    }[];
  }[];
};

@Component({
  selector: 'app-cbox-admin-locker-info',
  templateUrl: './cbox-admin-locker-info.component.html',
  styleUrls: ['./cbox-admin-locker-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TagModule,
    TableModule,
    CommonModule,
    ButtonModule,
    MatDialogClose,
    SkeletonModule,
    PaginatorModule,
    MeterGroupModule,
    MatDialogContent,
    CBoxToDoListComponent,
    ProgressSpinnerModule,
    MeterGroupModule,
    TagModule,
    FormsModule,
    DropdownModule,
    DividerModule,
    TooltipModule,
    CboxLockerPreviewComponent,
    FailedEventsTableComponent,
],
})
export class CBoxAdminLockerInfoComponent implements OnInit {
  modules = signal<CBox_AdminModuleListItemDataResponse[]>([]);
  modulesData = signal<
    Record<number, CBox_AdminLockerModuleDataResponse | undefined>
  >({});
  equipments = signal<ModuleEquipmentType[]>([]);
  equipmentsReady = signal(false);
  lockerData = signal<CBox_AdminLockerData | undefined>(undefined);
  lockerExtras = signal<
    | {
        isOldHeartbeat: boolean;
        memoryFreePercentage: number;
        storageFreePercentage: number;
      }
    | undefined
  >(undefined);

  // Command Status List
  commandStatusList = signal<CBoxPublicAdminGetCommandStatusListResponse>([]);
  commandStatusLoading = signal(false);
  commandStatusPagination = signal({
    page: 1,
    pageSize: 10,
    totalRecords: 0,
  });

  // Locker Preview
  lockerSVGs = signal<SafeHtml[]>([]);
  modulesIndexes = signal<number[]>([]);
  itemData = signal<LockerPreviewItemData>([]);

  dataFetched = signal<Record<string, boolean>>({
    todos: false,
    modules: false,
    lockerData: false,
  });
  loading = signal(true);
  memoryMeterItems = signal<MeterItem[]>([]);
  storageMeterItems = signal<MeterItem[]>([]);

  constructor(
    private api: ApiService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private toastService: ToastService,
    private dialogRef: MatDialogRef<CBoxAdminLockerInfoComponent>,
    private destroyRef: DestroyRef,
    @Inject(MAT_DIALOG_DATA) public lockerIdentifier: string
  ) {}

  ngOnInit(): void {
    this.init();
  }

  getModuleNecessaryEquipmentsCount(moduleId: number): number {
    return (
      this.modulesData()[moduleId]?.equipmentTypeCounts.reduce(
        (acc, equipmentType) => {
          if (equipmentType.counts.actual >= equipmentType.counts.minRequired) {
            return acc;
          }

          return acc + equipmentType.counts.minRequired;
        },
        0
      ) || 0
    );
  }

  openModuleDataDialog(moduleId: number): void {
    const dialog = this.dialog.open(CBoxAdminModuleInfoComponent, {
      minWidth: 'min(800px, 100%)',
      data: moduleId,
    });

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe(() => {
        this.fetchSingleModuleData(moduleId);
      });
  }

  copyAndCreateEquipment(equipment?: ModuleEquipmentType): void {
    if (!equipment) {
      return;
    }

    const dialog = this.dialog.open(CBoxAdminEquipmentCreateDialogComponent, {
      data: equipment,
      width: 'min(600px, 100%)',
      autoFocus: false,
    });

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe(() => {
        this.equipmentsReady.set(false);
        this.fetchSingleModuleData(equipment.module.id);
      });
  }

  getEquipmentMenuItems(equipment: ModuleEquipmentType): MenuItem[] {
    return [
      {
        label: 'Creează echipament similar',
        icon: 'pi pi-plus',
        command: () => {
          this.copyAndCreateEquipment(equipment);
        },
      },
    ];
  }

  createEquipmentToDo(equipment: any): void {
    const dialog = this.dialog.open(CBoxToDoCreateDialogComponent, {
      data: {
        equipment: {
          id: equipment.id,
          typeName: equipment.type.name,
        },
      },
      width: 'min(600px, 100%)',
      autoFocus: false,
    });

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe(() => {
        this.fetchTodos();
      });
  }

  configLocker(): void {
    const dialog = this.dialog.open(CBoxAdminLockerConfigDialogComponent, {
      data: this.lockerData(),
      width: 'min(600px, 100%)',
      disableClose: true,
      autoFocus: false,
    });

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe(() => {
        this.dataFetched.update((fetched) => {
          fetched['lockerData'] = false;
          return fetched;
        });
        this.fetchLockerData();
      });
  }

  lockerCleanAddress(): string {
    return lockerCleanAddress(this.lockerData()?.addressParts!) || '';
  }

  updateLocker(): void {
    const confirmation = this.dialog.open(
      CBoxProfileLockerUpdateConfirmationDialogComponent,
      {
        data: this.lockerIdentifier,
      }
    );

    confirmation
      .afterClosed()
      .pipe(first())
      .subscribe((confirmed) => {
        if (confirmed) {
          this.performLockerUpdate();
        }
      });
  }

  formatTimeInMilliseconds(ms: number | undefined): string {
    if (!ms) {
      return '0';
    }
    return formatTimeInMilliseconds(ms);
  }

  formatDate(date: Date | undefined): string {
    if (!date) {
      return '';
    }
    return formatDate(date, 'dd-MM-yyyy HH:mm', 'ro-RO', 'Europe/Bucharest');
  }

  updateEquipmentsStatus(): void {
    const dialog = this.dialog.open(
      CBoxAdminLockerEquipmentStatusUpdateDialogComponent,
      {
        data: structuredClone(this.lockerData()),
        minWidth: 'min(600px, 100%)',
      }
    );

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe((lockerData: CBox_AdminLockerData) => {
        if (lockerData) {
          this.performEquipmentStatusUpdate(lockerData.options);
        }
      });
  }

  private performEquipmentStatusUpdate(
    components: CBox_AdminLockerData['options']
  ): void {
    this.api
      .patch(
        'backend/internal/lockers?lockerIdentifier=' +
          this.lockerData()?.externalIdentifier,
        {
          components: Object.entries(components).map((componentEntry) => ({
            type: componentEntry[0].toUpperCase(),
            state: componentEntry[1].state,
          })),
        }
      )
      .subscribe(
        () => {
          this.toastService.showSuccessToast(
            'Confirmare',
            'Starea componentei a fost actualizata cu succes!'
          );
          this.fetchLockerData();
        },
        (e: HttpErrorResponse) => {
          handlePublicApiError(e, this.toastService);
        }
      );
  }

  private performLockerUpdate(): void {
    this.api
      .put('backend/internal/locker/update', {
        lockerIdentifier: this.lockerIdentifier,
      })
      .subscribe(
        (response) => {
          this.toastService.showSuccessToast(
            'Confirmare',
            'Locker-ul este in proces de actualizare.'
          );
        },
        (e: HttpErrorResponse) => {
          handlePublicApiError(e, this.toastService);
        }
      );
  }

  public init(): void {
    interval(5_000)
      .pipe(
        startWith(0),
        switchMap(() => this.fetchAllDataInParallel()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: (err) => console.error('data refresh failed', err),
      });
  }

  private fetchAllDataInParallel(): Observable<any> {
    const lockerData$ = this.fetchLockerData();
    const todos$ = this.fetchTodos();
    const commandList$ = this.fetchCommandStatusList();
    const modules$ = this.fetchModules().pipe(
      switchMap((modulesList) => {
        if (!modulesList || modulesList['data'].length === 0) {
          return of([]);
        }

        const moduleDetailObservables = modulesList.data.map((module) =>
          this.fetchSingleModuleData(module.id)
        );
        return forkJoin(moduleDetailObservables);
      })
    );

    return forkJoin([lockerData$, todos$, modules$, commandList$]);
  }

  private fetchLockerData() {
    const httpParams = new HttpParams({
      fromObject: {
        lockerIdentifier: this.lockerIdentifier,
        includeInternalIdentifier: true,
        includeStructure: true,
      },
    });

    return this.api
      .get<CBox_PublicSuccessResponse<CBox_AdminLockerData>>(
        'backend/internal/locker',
        httpParams
      )
      .pipe(
        tap((response) => {
          if (this.isDifferent(this.lockerData(), response.data)) {
            this.lockerData.set(response.data);
          }
          this.dataFetched.update((fetched) => {
            fetched['lockerData'] = true;
            return fetched;
          });
          this.setLockersSvg(response.data);
          this.createLockerExtras(response.data);
          this.createMeterItems(response.data);
          this.ready();
        }),
        catchError((e: HttpErrorResponse) => {
          handlePublicApiError(e, this.toastService);
          this.dialogRef.close();
          return throwError(() => e);
        })
      );
  }

  private fetchTodos() {
    return this.api
      .get<CBox_PublicSuccessResponse<CBox_AdminGetTodoListDataItem>>(
        'backend/internal/todo',
        this.getToDoSearchStructure()
      )
      .pipe(
        tap((response) => {
          this.dataFetched.update((fetched) => {
            fetched['todos'] = true;
            return fetched;
          });
          this.ready();
        }),
        catchError((e: HttpErrorResponse) => {
          handlePublicApiError(e, this.toastService);
          this.dialogRef.close();
          return throwError(() => e);
        })
      );
  }

  private createLockerExtras(lockerData: CBox_AdminLockerData): void {
    const isOldHeartbeat = this.hasMinutesPassedSince(
      lockerData.heartbeat?.lastPingAt,
      2
    );
    const memoryFreePercentage = this.calculatePercentageFromTotal(
      lockerData?.heartbeat?.data?.memory?.available || 0,
      lockerData?.heartbeat?.data?.memory?.total || 1
    );
    const storageFreePercentage = this.calculatePercentageFromTotal(
      lockerData?.heartbeat?.data?.storage?.available || 0,
      lockerData?.heartbeat?.data?.storage?.total || 1
    );
    this.lockerExtras.set({
      isOldHeartbeat,
      memoryFreePercentage,
      storageFreePercentage,
    });
  }

  private createMeterItems(lockerData: CBox_AdminLockerData): void {
    const memoryMeterItems: MeterItem[] = [];
    const storageMeterItems: MeterItem[] = [];

    if (lockerData?.heartbeat?.data?.memory) {
      const { total, available } = lockerData.heartbeat.data.memory;
      memoryMeterItems.push({
        label: 'Liber',
        value: this.calculatePercentageFromTotal(available, total),
        color: 'var(--green-500)',
      });

      memoryMeterItems.push({
        label: 'Ocupat',
        value: this.calculatePercentageFromTotal(total - available, total),
        color: 'var(--red-500)',
      });
    }

    if (lockerData?.heartbeat?.data?.storage) {
      const { total, available } = lockerData.heartbeat.data.storage;
      storageMeterItems.push({
        label: 'Liber',
        value: this.calculatePercentageFromTotal(available, total),
        color: 'var(--green-500)',
      });

      storageMeterItems.push({
        label: 'Ocupat',
        value: this.calculatePercentageFromTotal(total - available, total),
        color: 'var(--red-500)',
      });
    }

    this.storageMeterItems.set(storageMeterItems);
    this.memoryMeterItems.set(memoryMeterItems);
  }

  private calculatePercentageFromTotal(value: number, total: number): number {
    if (total === 0) {
      return 0;
    }

    return (value / total) * 100;
  }

  hasMinutesPassedSince(date: Date | undefined, minutes: number): boolean {
    if (!date) {
      return false;
    }
    return hasMinutesPassedSince(date, minutes);
  }

  getHashMatchValue(
    type: CBox_AdminLockerData['state']['hashes'][number]['type']
  ): CBox_AdminLockerData['state']['hashes'][number]['matchState'] {
    return this.lockerData()?.state.hashes.find((h) => h.type === type)
      ?.matchState!;
  }

  private fetchModules() {
    return this.api
      .get<CBox_PublicSuccessResponse<CBox_AdminModuleListItemDataResponse[]>>(
        'backend/internal/modules/list?lockerIdentifier=' +
          this.lockerIdentifier
      )
      .pipe(
        tap((response) => {
          this.modules.set(response.data);
          this.dataFetched.update((fetched) => {
            fetched['modules'] = true;
            return fetched;
          });
          this.ready();

          // for (const module of response.data) {
          //   this.modulesData.update((data) => {
          //     data[module.id] = undefined;
          //     return data;
          //   });
          // }
        }),
        catchError((error) => throwError(() => error))
      );
  }

  private fetchAllModulesData(): Observable<any> {
    const moduleIds = this.modules().map((module) => module.id);

    if (moduleIds.length === 0) {
      return of(null);
    }

    const moduleObservables = moduleIds.map((id) =>
      this.fetchSingleModuleData(id)
    );

    return forkJoin(moduleObservables);
  }

  private fetchSingleModuleData(moduleId: number) {
    const oldData = this.modulesData()[moduleId];
    // this.modulesData.update((data) => {
    //   delete data[moduleId];
    //   return data;
    // });

    return this.api
      .get<CBox_PublicSuccessResponse<CBox_AdminLockerModuleDataResponse>>(
        'backend/internal/modules?moduleId=' + moduleId
      )
      .pipe(
        tap((response) => {
          this.modulesData.update((data) => {
            data[moduleId] = response.data;
            return data;
          });
          this.moduleReady();
        }),
        catchError((error) => {
          this.modulesData.update((data) => {
            data[moduleId] = oldData;
            return data;
          });
          return throwError(() => error);
        })
      );
  }

  private createEquipmentsDatasource(): void {
    const equipments: ModuleEquipmentType[] = [];
    for (const moduleId of Object.keys(this.modulesData())) {
      const moduleData = this.modulesData()[+moduleId];
      if (moduleData?.equipments) {
        for (const equipment of moduleData.equipments) {
          equipments.push({
            ...equipment,
            module: {
              serial: moduleData.serial,
              id: +moduleId,
            },
          } as ModuleEquipmentType);
        }
      }
    }

    this.equipments.set(equipments);
    this.equipmentsReady.set(true);
  }

  private ready(): void {
    const isReady = Object.values(this.dataFetched()).every(
      (value) => value === true
    );
    return this.loading.set(!isReady);
  }

  private moduleReady(): void {
    const isReady = Object.values(this.modulesData()).every(
      (value) => value !== undefined
    );
    if (isReady) {
      this.createEquipmentsDatasource();
    }
  }

  private getToDoSearchStructure(): CBox_AdminGetTodoListDataParams {
    return Object.entries({
      'lockerIdentifiers[]': [this.lockerIdentifier],
      page: 1,
      pageSize: 25,
    })
      .filter(([_, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }

        return value !== null && value !== undefined;
      })
      .reduce((acc, [key, value]) => {
        acc[key as keyof CBox_AdminGetTodoListDataParams] =
          value as CBox_AdminGetTodoListDataParams[keyof CBox_AdminGetTodoListDataParams];
        return acc;
      }, {} as any);
  }

  setLockersSvg(lockerData: CBox_AdminLockerData): void {
    if (!lockerData.structure || lockerData.structure.modules.length === 0) {
      return;
    }

    this.itemData.set(lockerData.boxesData);
    const sortedModulesByPositions = lockerData.structure.modules.sort(
      (a, b) => a.position - b.position
    );

    let svgList: SafeHtml[] = [];
    for (const module of sortedModulesByPositions) {
      if (module) {
        const svg = module.assets.find((asset) => asset.type === 'SVG_LAYOUT');
        if (svg) {
          const safeSvg = this.sanitizer.bypassSecurityTrustHtml(svg.content);
          svgList.push(safeSvg);
          this.modulesIndexes.update((indexes) => {
            indexes.push(module.boardId);
            return indexes;
          });
        }
      }
    }

    this.lockerSVGs.set(svgList);
  }

  fetchCommandStatusList(): Observable<any> {
    this.commandStatusLoading.set(true);

    const body: CBoxPublicAdminGetCommandStatusListParams = {
      lockerIdentifier: this.lockerIdentifier,
      page: this.commandStatusPagination().page,
      pageSize: this.commandStatusPagination().pageSize,
    };

    return this.api
      .post<
        CBox_PublicPaginatedResponse<
          CBoxPublicAdminGetCommandStatusListResponse[0]
        >
      >('backend/internal/locker/commands/status/list', body)
      .pipe(
        tap((response) => {
          this.commandStatusList.set(response.data);
          this.commandStatusPagination.update((pagination) => ({
            ...pagination,
            totalRecords: response?.totalCount || 0,
          }));
          this.commandStatusLoading.set(false);
        }),
        catchError((e: HttpErrorResponse) => {
          handlePublicApiError(e, this.toastService);
          this.commandStatusLoading.set(false);
          return throwError(() => e);
        })
      );
  }

  onCommandPageChange(event: any): void {
    if (this.commandStatusLoading()) {
      return;
    }
    this.commandStatusLoading.set(true);
    const page = event.page + 1;
    const pageSize = event.rows;

    this.commandStatusPagination.update((pagination) => ({
      ...pagination,
      page,
      pageSize,
    }));
    this.fetchCommandStatusList().subscribe();
  }

  openCommandDetails(
    command: CBoxPublicAdminGetCommandStatusListResponse[number]
  ): void {
    const dialog = this.dialog.open(CBoxAdminLockerCommandDetailsComponent, {
      data: command,
      width: 'min(800px, 100%)',
      autoFocus: false,
    });
  }

  getCommandStateSeverity(state: LockerAdminActionLogState) {
    switch (state) {
      case LockerAdminActionLogState.FINISHED_SUCCESS:
        return 'success';
      case LockerAdminActionLogState.FINISHED_ERROR:
      case LockerAdminActionLogState.CANCELED:
        return 'danger';
      case LockerAdminActionLogState.IN_PROGRESS:
      case LockerAdminActionLogState.INITIAL:
        return 'warning';
      default:
        return 'info';
    }
  }

  getDoorActionStateSeverity(state: LockerAdminOpenDoorActionLogState) {
    switch (state) {
      case LockerAdminOpenDoorActionLogState.FINISHED_SUCCESS:
        return 'success';
      case LockerAdminOpenDoorActionLogState.FINISHED_ERROR:
      case LockerAdminOpenDoorActionLogState.FINISHED_TIMEOUT:
        return 'danger';
      case LockerAdminOpenDoorActionLogState.IN_PROGRESS:
      case LockerAdminOpenDoorActionLogState.INITIAL:
        return 'warning';
      default:
        return 'info';
    }
  }

  openUpdateDialog(equipment: CBox_AdminEquipmentListItem): void {
    const dialog = this.dialog.open(
      CboxAdminLockerActionConfirmationDialogComponent,
      {
        data: {
          header: 'Actualizare echipament',
          message: 'Ești sigur că vrei să actualizezi echipamentul?',
          note: true,
          actionType: 'other',
          securityKeyword: 'CONFIRM',
        },
        width: 'min(600px, 100%)',
        autoFocus: false,
      }
    );

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe((data: { reason: string; confirmed: boolean }) => {
        if (!data.confirmed) {
          return;
        }

        this.api
          .put<CBox_PublicSuccessResponse<void>>(
            `backend/internal/locker/update/card-terminal/${this.lockerIdentifier}`,
            { note: data.reason }
          )
          .subscribe({
            next: () => {
              this.toastService.showSuccessToast(
                'Confirmare',
                'Actualizare reușită!'
              );
              this.fetchLockerData();
            },
            error: (e: HttpErrorResponse) => {
              handlePublicApiError(e, this.toastService);
            },
          });
      });
  }

  settleCardTerminal(equipment: CBox_AdminEquipmentListItem): void {
    const dialog = this.dialog.open(
      CboxAdminLockerActionConfirmationDialogComponent,
      {
        data: {
          header: 'Confirmare settlement terminal card',
          message: 'Ești sigur că vrei să dai settle la terminalul de card?',
          note: false,
          actionType: 'other',
          securityKeyword: 'CONFIRM',
        },
        width: 'min(600px, 100%)',
        autoFocus: false,
      }
    );

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe((data: { reason: string; confirmed: boolean }) => {
        if (!data.confirmed) {
          return;
        }

        this.api
          .put<CBox_PublicSuccessResponse<void>>(
            `backend/internal/locker/settle/card-terminal/${this.lockerIdentifier}`,
            { note: data.reason }
          )
          .subscribe({
            next: () => {
              this.toastService.showSuccessToast(
                'Confirmare',
                'Actualizare reușită!'
              );
              this.fetchLockerData();
            },
            error: (e: HttpErrorResponse) => {
              handlePublicApiError(e, this.toastService);
            },
          });
      });
  }

  openSoftwareUpdateDialog(): void {
    const dialogRef = this.dialog.open(
      CboxAdminLockerActionConfirmationDialogComponent,
      {
        data: {
          header: 'Confirmare actualizare software',
          message: 'Ești sigur că vrei să actualizezi software-ul?',
          note: false,
          actionType: 'other',
          securityKeyword: 'CONFIRM SOFTWARE UPDATE',
        },
        width: 'min(600px, 100%)',
        autoFocus: false,
      }
    );

    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe((data: { reason: string; confirmed: boolean }) => {
        if (!data.confirmed) {
          return;
        }

        this.api
          .post<CBox_PublicSuccessResponse<{ success: true }>>(
            'backend/internal/locker/commands/software/update?lockerIdentifier=' +
              this.lockerIdentifier,
            {}
          )
          .subscribe({
            next: () => {
              this.toastService.showSuccessToast(
                'Confirmare',
                'Actualizare reușită!'
              );
            },
            error: (e: HttpErrorResponse) => {
              handlePublicApiError(e, this.toastService);
            },
          });
      });
  }

  private isDifferent(a: any, b: any): boolean {
    if (!a || !b) {
      return true;
    }
    return !!!(JSON.stringify(a) === JSON.stringify(b));
  }

  hasKeys(object: any): boolean {
    return Object.keys(object).length > 0;
  }
}
